import React, {
  createRef,
  forwardRef,
  Key,
  useCallback,
  useImperativeHandle,
  useEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import cls from 'classnames';

import useWebApi from '../../hooks/useWebApi';
import { ContextHolderRef, InnerMessageConfig, Placements, Placement } from '../../types';
import { Message } from './blocks/Message';

import styles from './MessagesList.scss';

const topClassNames = {
  appear: styles.topEnter,
  appearActive: styles.topEnterActive,
  enter: styles.topEnter,
  enterActive: styles.topEnterActive,
  exit: styles.topExit,
  exitActive: styles.topExitActive,
};

const bottomClassNames = {
  appear: styles.bottomEnter,
  appearActive: styles.bottomEnterActive,
  enter: styles.bottomEnter,
  enterActive: styles.bottomEnterActive,
  exit: styles.bottomExit,
  exitActive: styles.bottomExitActive,
};

const DEFAULT_MARGIN = 8;

export const MessagesList = forwardRef<ContextHolderRef>((_, ref) => {
  const [messagesList, setMessagesList] = useState<InnerMessageConfig[]>([]);

  const { document } = useWebApi();

  const handleCloseMessage = useCallback((key: Key) => {
    const messageWithThatKey = messagesList.find((message) => message.key === key);

    messageWithThatKey?.onClose?.();

    setMessagesList((current) => {
      return current.filter((message) => message.key !== key);
    });
  }, []);

  // реализовал через js, чтобы была возможность корректно проигрывать анимацию для
  // плашки с ЛЮБОЙ высотой, через css же такого сделать не получится
  const playExitAnimation = (message: InnerMessageConfig, placement: Placement) => {
    const node = message.nodeRef.current;

    if (node) {
      const { height } = node.getBoundingClientRect();

      const isTopPlacement = ['topLeft', 'top', 'topRight'].includes(placement);

      const margin = document
        ? parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--message-vertical-margin')
          )
        : DEFAULT_MARGIN;

      if (isTopPlacement) {
        node.style.marginTop = `-${height + margin}px`;
      } else {
        node.style.marginBottom = `-${height + margin}px`;
      }
    }
  };

  const cancelExitAnimation = (message: InnerMessageConfig) => {
    const node = message.nodeRef.current;

    if (node) {
      if (!!node.style.marginTop) {
        node.style.marginTop = '';
      } else if (!!node.style.marginBottom) {
        node.style.marginBottom = '';
      }
    }
  };

  useImperativeHandle(ref, () => ({
    open: (config) => {
      setMessagesList((list) => {
        let clonedList = [...list];

        // Replace if exist
        const index = clonedList.findIndex((message) => message.key === config.key);
        const configWithUpdateCount: InnerMessageConfig = {
          ...config,
          timesUpdated: 0,
          nodeRef: createRef<HTMLDivElement>(),
        };

        if (index >= 0) {
          configWithUpdateCount.timesUpdated = list[index].timesUpdated + 1;
          configWithUpdateCount.nodeRef = list[index].nodeRef;
          clonedList[index] = configWithUpdateCount;
        } else {
          clonedList.push(configWithUpdateCount);
        }

        return clonedList;
      });
    },
    close: (key) => {
      handleCloseMessage(key);
    },
    destroy: () => {
      setMessagesList([]);
    },
  }));

  const [placements, setPlacements] = useState<Placements>({});

  useEffect(() => {
    const nextPlacements: Placements = {};

    messagesList.forEach((config) => {
      const { placement = 'bottomRight' } = config;

      nextPlacements[placement] = nextPlacements[placement] || [];
      nextPlacements[placement]!.push(config);
    });

    // Fill exist placements to avoid empty list causing remove without animation
    Object.keys(placements).forEach((placement) => {
      nextPlacements[placement as Placement] = nextPlacements[placement as Placement] || [];
    });

    setPlacements(nextPlacements);
  }, [messagesList]);

  const placementList = Object.keys(placements) as Placement[];

  if (!document) {
    return null;
  }

  return createPortal(
    <>
      {placementList.map((placement) => {
        const placementConfigList = placements[placement]!;

        const isTopPlacement = ['topLeft', 'top', 'topRight'].includes(placement);

        return (
          <TransitionGroup
            appear={true}
            key={placement}
            className={cls(styles.container, styles[placement])}
            data-locator={`messages-container-${placement}`}
          >
            {placementConfigList.map((config) => (
              <CSSTransition
                appear={true}
                key={config.key}
                nodeRef={config.nodeRef}
                timeout={300}
                onExit={() => playExitAnimation(config, placement)}
                onEnter={() => cancelExitAnimation(config)}
                classNames={isTopPlacement ? topClassNames : bottomClassNames}
              >
                <Message
                  ref={config.nodeRef}
                  config={config}
                  onClose={handleCloseMessage}
                  isTopPlacement={isTopPlacement}
                />
              </CSSTransition>
            ))}
          </TransitionGroup>
        );
      })}
    </>,
    document.body
  );
});
